import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertObligationOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertObligationOversightResultInput = {
  OversightAssessmentId: string;
  ObligationIds: string[];
  // COI-650: V2 required Rating on this insert; the form schema has always
  // made it mandatory, so the type now matches.
  Rating: number;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertObligationOversightResultMutation => ({
  insertChildObligationOversightResult: {
    __typename: 'InsertChildObligationAssessmentResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertObligationOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertObligationOversightResult.mutationOptions(
      {
        onSuccess: async () => {
          await Promise.all([
            // Apollo parity: evictField(cache, 'obligation_oversight_result')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.obligationOversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.pathKey(),
            }),
            // Apollo parity: evictField(cache, 'obligation_oversight_result_aggregate')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
            }),
            // Apollo parity: evictField(cache, 'oversight_assessment')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityRegister.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityCount.queryKey(),
            }),
          ]);
        },
      }
    ),
  });

  return {
    insertObligationOversightResult: async (
      variables: InsertObligationOversightResultInput
    ): Promise<InsertObligationOversightResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
